import { ref, watch, onMounted } from "vue"

type Theme = "light" | "dark"

const THEME_STORAGE_KEY = "dinecloud-theme"

// Get theme from localStorage or default to light
const getStoredTheme = (): Theme => {
  if (typeof window === "undefined") return "light"
  const stored = localStorage.getItem(THEME_STORAGE_KEY) as Theme | null
  return stored === "dark" ? "dark" : "light"
}

// Apply theme to HTML element
const applyTheme = (theme: Theme) => {
  if (typeof document === "undefined") return

  const html = document.documentElement

  if (theme === "dark") {
    html.classList.add("dark")
  } else {
    html.classList.remove("dark")
  }
}

// Initialize theme on mount
export const useTheme = () => {
  const theme = ref<Theme>(getStoredTheme())

  // Apply theme immediately
  onMounted(() => {
    applyTheme(theme.value)
  })

  // Watch for theme changes and apply them
  watch(
    theme,
    newTheme => {
      applyTheme(newTheme)
      localStorage.setItem(THEME_STORAGE_KEY, newTheme)
    },
    { immediate: true }
  )

  const setTheme = (newTheme: Theme) => {
    theme.value = newTheme
  }

  const toggleTheme = () => {
    theme.value = theme.value === "light" ? "dark" : "light"
  }

  return {
    theme,
    setTheme,
    toggleTheme,
  }
}

// Initialize theme on module load (for SSR compatibility)
if (typeof document !== "undefined") {
  const initialTheme = getStoredTheme()
  applyTheme(initialTheme)
}

